import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MAX_RECENT_SEARCH_RESULTS } from 'uniswap/src/components/TokenSelector/constants'
import { currencyInfosToTokenOptions } from 'uniswap/src/components/TokenSelector/hooks/useCurrencyInfosToTokenOptions'
import { TokenOption } from 'uniswap/src/components/lists/items/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { CurrencyInfo } from 'uniswap/src/features/dataApi/types'
import { SearchResultType, TokenSearchResult } from 'uniswap/src/features/search/SearchResult'
import { selectSearchHistory } from 'uniswap/src/features/search/selectSearchHistory'
import { useCurrencyInfos } from 'uniswap/src/features/tokens/useCurrencyInfo'
import { buildCurrencyId, buildNativeCurrencyId } from 'uniswap/src/utils/currencyId'

export function useRecentlySearchedTokens(
  chainFilter: UniverseChainId | null,
  numberOfResults = MAX_RECENT_SEARCH_RESULTS,
): TokenOption[] {
  const searchHistory = useSelector(selectSearchHistory)

  const recentTokenSearchHistory = useMemo(
    () =>
      searchHistory
        .filter((searchResult): searchResult is TokenSearchResult => searchResult.type === SearchResultType.Token)
        .filter((searchResult) => (chainFilter ? searchResult.chainId === chainFilter : true))
        .slice(0, numberOfResults),
    [chainFilter, numberOfResults, searchHistory],
  )
  const searchHistoryCurrencyInfos = useSearchHistoryToCurrencyInfos(recentTokenSearchHistory)

  return useMemo(() => {
    return currencyInfosToTokenOptions(searchHistoryCurrencyInfos) ?? []
  }, [searchHistoryCurrencyInfos])
}

// TODO(WEB-5131): Clean up searchHistory slice so that we only save chainId & address to redux
function useSearchHistoryToCurrencyInfos(searchHistory: TokenSearchResult[]): Maybe<CurrencyInfo>[] {
  const currencyIds = searchHistory.map((searchResult) => {
    return searchResult.address
      ? buildCurrencyId(searchResult.chainId, searchResult.address)
      : buildNativeCurrencyId(searchResult.chainId)
  })

  const currencyInfos = useCurrencyInfos(currencyIds)

  return useMemo(
    () =>
      currencyInfos.map((currencyInfo, index) => {
        if (!currencyInfo) {
          return currencyInfo
        }

        const logoUrl = currencyInfo.logoUrl || searchHistory[index]?.logoUrl
        return logoUrl === currencyInfo.logoUrl ? currencyInfo : { ...currencyInfo, logoUrl }
      }),
    [currencyInfos, searchHistory],
  )
}
